import { Trans, useTranslation } from "@i18n";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getImageUrl } from "@Utils";
import { Link } from "@mui/material";
import { RootState } from "@Reducers/index";
import type React from "react";

/** The three legacy fullscreen attributes the embedded demo iframes still carry; React has none of them. */
type NonStandardIframeAttrs = React.IframeHTMLAttributes<HTMLIFrameElement> & {
  webkitallowfullscreen?: boolean;
  mozallowfullscreen?: boolean;
  allowfullscreen?: boolean;
};

function Welcome() {
  const { t } = useTranslation();
  const portalData = useSelector((state: RootState) => state.portalData);
  const location = useLocation();
  const [showTutorial, setShowTutorial] = useState(false);
  const [activeSection, setActiveSection] = useState("createProject");
  const commonClass = "pt-8 pe-10 pb-8 ps-4 border-button_light border-solid border cursor-pointer";
  const activeClass = "border-solid border-s-4 border-y-2 border-e-0 border-gradient_color";
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash == "#tutorial") {
      setShowTutorial(true);
    }
  }, [location.hash]);

  const handleDashboardRedirect = () => {
    navigate("/dashboard");
  };

  const handleDocumentationRedirect = () => {
    window.open("https://support.cube.ms/space/CKB", "_blank");
  };

  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  const handleHideTutorial = () => {
    setShowTutorial(false);
  };

  const handleSectionClick = (event: React.MouseEvent<HTMLElement>) => {
    // The listener sits on the section element itself, so the event target is that element.
    setActiveSection((event.target as HTMLElement).id);
  };

  const createProjectDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(56.25% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/S0mZYXHXLoHpTx2pDYZq?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: "0",
          insetInlineStart: "0",
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Creating a new Project",
        }}
      />
    </div>
  );

  const createSpaceDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(56.25% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/P1QbpOjfoJF2Ax4w0FGW?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: 0,
          insetInlineStart: 0,
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Creating a Space",
        }}
      />
    </div>
  );

  const addUserToProjectDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(56.25% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/tpmTFxL7bJ84lvpfLZzv?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: "0",
          insetInlineStart: "0",
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Adding Users to the Project",
        }}
      />
    </div>
  );

  const addUserToSpaceDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(56.25% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/xSo57jShO56QjRMzNMCk?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: "0",
          insetInlineStart: "0",
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Adding users to Space",
        }}
      />
    </div>
  );

  const folderManagmentDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(56.25% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/ghtP7xgELSuRCuNsTUH6?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: "0",
          insetInlineStart: "0",
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Folder Management and Managing files.",
        }}
      />
    </div>
  );

  const fileViewAndUploadDiv = () => (
    <div
      style={{
        position: "relative",
        paddingBottom: "calc(48.50694444444444% + 2.5625rem)",
        height: "0",
        width: "100%",
      }}
    >
      <iframe
        title={t("TODO: add appropriate title")}
        src="https://demo.arcade.software/WrQrySWzf7IH4SKjwLE5?embed"
        frameBorder="0"
        loading="lazy"
        {...({
          webkitallowfullscreen: true,
          mozallowfullscreen: true,
          allowfullscreen: true,
        } as NonStandardIframeAttrs)}
        style={{
          position: "absolute",
          top: "0",
          insetInlineStart: "0",
          width: "100%",
          height: "100%",
          colorScheme: "light" as React.CSSProperties["colorScheme"],
          // title: "Uploading and viewing a file",
        }}
      />
    </div>
  );

  const content = () => (
    <div className="flex w-full h-125">
      <div className="flex h-full w-1/3 flex-col overflow-auto">
        <div
          id="createProject"
          onClick={handleSectionClick}
          className={`${commonClass}  ${activeSection === "createProject" ? activeClass : ""}`}
        >
          {t("Creating a New Project")}
        </div>
        <div
          id="createSpace"
          onClick={handleSectionClick}
          className={`${commonClass} ${activeSection === "createSpace" ? activeClass : ""}`}
        >
          {t("Creating a Space")}
        </div>
        <div
          id="addUserToProject"
          onClick={handleSectionClick}
          className={`${commonClass} ${activeSection === "addUserToProject" ? activeClass : ""}`}
        >
          {t("Adding Users to the Project")}
        </div>
        <div
          id="addUserToSpace"
          onClick={handleSectionClick}
          className={`${commonClass} ${activeSection === "addUserToSpace" ? activeClass : ""}`}
        >
          {t("Adding Users to the Space")}
        </div>
        <div
          id="folderManagment"
          onClick={handleSectionClick}
          className={`${commonClass} ${activeSection === "folderManagment" ? activeClass : ""}`}
        >
          {t("Folder Management and Managing Files")}
        </div>
        <div
          id="fileViewAndUpload"
          onClick={handleSectionClick}
          className={`${commonClass} ${activeSection === "fileViewAndUpload" ? activeClass : ""}`}
        >
          {t("Uploading and Viewing a File")}
        </div>
      </div>
      <div className="flex h-full w-2/3 overflow-auto">
        {activeSection === "createProject" && createProjectDiv()}
        {activeSection === "createSpace" && createSpaceDiv()}
        {activeSection === "addUserToProject" && addUserToProjectDiv()}
        {activeSection === "addUserToSpace" && addUserToSpaceDiv()}
        {activeSection === "folderManagment" && folderManagmentDiv()}
        {activeSection === "fileViewAndUpload" && fileViewAndUploadDiv()}
      </div>
    </div>
  );

  return (
    <>
      <div className="flex h-full w-full flex-col items-center justify-around">
        <div className="flex h-full w-full flex-col items-center gap-6 overflow-auto py-6">
          <img className="w-36 xl:pb-4" src={`${getImageUrl()}/logoAndText.png`} />
          <div className="flex flex-col items-center gap-6">
            <img className="w-96 xl:w-96" src={`${getImageUrl()}/welcome.svg`} />
            <div className="flex flex-col items-center gap-2">
              <span className="text-4xl font-bold">{`Welcome to the ${portalData.name} Portal!`}</span>
              <span className="text-2xl font-semibold">{t("You are on the PRO plan. Next you can:")}</span>
            </div>
          </div>
          <div className="flex w-[80%] justify-evenly">
            <div
              className="flex h-[12.75rem] w-[15rem] cursor-pointer flex-col items-center justify-evenly rounded-[0.625rem] bg-white shadow-[0.25rem_0.25rem_0.9375rem_rgb(65_92_171_/_15%)]"
              onClick={handleDashboardRedirect}
            >
              <img className="w-fit" src={`${getImageUrl()}/four_square.svg`} />
              <span className="px-[1.875rem] text-center">{t("Access your Dashboard")}</span>
            </div>
            <div
              className="flex h-[12.75rem] w-[15rem] cursor-pointer flex-col items-center justify-evenly rounded-[0.625rem] bg-white shadow-[0.25rem_0.25rem_0.9375rem_rgb(65_92_171_/_15%)]"
              onClick={handleShowTutorial}
            >
              <img className="w-fit" src={`${getImageUrl()}/media.svg`} />
              <span className="px-[1.875rem] text-center">
                {t("View a Tutorial on how to create a project and get started.")}
              </span>
            </div>
            <div
              className="flex h-[12.75rem] w-[15rem] cursor-pointer flex-col items-center justify-evenly rounded-[0.625rem] bg-white shadow-[0.25rem_0.25rem_0.9375rem_rgb(65_92_171_/_15%)]"
              onClick={handleDocumentationRedirect}
            >
              <img className="w-fit" src={`${getImageUrl()}/document.svg`} />
              <span className="px-[1.875rem] text-center">
                {t("Access our Knowledge Base - A complete Guide to CUBE")}
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col items-center justify-center text-sm">
            <span>
              {t(
                "As a part of the Pro plan, you can access Document Management, CAD & BIM Viewer, create and track issues, send transmittals and more.",
              )}
            </span>
            <span>
              <Trans
                i18nKey="enterprisePlanText"
                components={{
                  enterpriseLink: (
                    <Link
                      underline="hover"
                      className="text-primary_gradient_color1 font-semibold whitespace-nowrap cursor-pointer"
                      href="https://cube.ms/enterprise"
                      target="_blank"
                    />
                  ),
                }}
              />
            </span>
          </div>
        </div>
      </div>
      <ModalStateDialogComponent
        isVisible={showTutorial}
        width="90%"
        headerTitle={t("Get Started With Cube")}
        modelContent={content()}
        onCancel={handleHideTutorial}
      />
      <div />
    </>
  );
}

export default Welcome;
